import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { EndUserWithId } from '@app/core/interfaces/c5/end-user';
import { SiteLocal } from '@app/core/interfaces/site-local';
import { BulkActionEntry } from '@app/shared/models/bulk-action-entry';
import { BulkActionEntryState } from '@app/shared/models/bulk-action-entry-state';
import { BulkActionInput } from '@app/shared/models/bulk-action-input';
import { BulkActionState } from '@app/shared/models/bulk-action-state';
import { FilterableEntry } from '@app/shared/models/filterable-entry';
import { Page } from '@app/shared/models/page';
import { BulkActionService } from '@app/shared/services/bulk-action.service';
import { UnsavedChangesService } from '@app/shared/services/unsaved-changes-service';
import { toMap } from '@app/utils/common';
import { PortalError } from '@app/utils/error/portal-error';
import { MetaDataProperty } from '@app/utils/lists/meta-list/meta-data-property';
import { loggerFor } from '@app/utils/logger';
import { StringMap } from '@app/utils/string-map';
import { templateRefToText } from '@app/utils/template-ref-to-text';

@Component({
  selector: 'portal-bulk-action',
  templateUrl: './bulk-action.component.html',
  styles: [],
  host: {
    class: 'bulk-action',
  },
  standalone: false,
})
export class BulkActionComponent implements OnChanges, OnInit {
  /**
   * Only relevant if the mode is ADD_REMOVE
   */
  @Input()
  public activeEntries: EndUserWithId[];

  @Input()
  public bodyTemplate: TemplateRef<any>;

  @Input()
  public collapsible: boolean = false;

  @Input()
  public entries: EndUserWithId[];

  @Input()
  public errorTemplate?: TemplateRef<any>;

  @Input()
  public headerTemplate: TemplateRef<any>;

  @Input()
  public mainActionText: string;

  @Input()
  public mode: 'ADD' | 'REMOVE' | 'ADD_REMOVE' = 'ADD';

  @Input()
  public processingFunction: (
    entry: BulkActionEntry,
    onSuccess: () => void,
    onFailure: (error: PortalError) => void
  ) => void;

  @Input()
  public replay: boolean = false;

  @Input()
  public sites: SiteLocal[] = [];

  @Input()
  public template: TemplateRef<any>;

  @Input()
  public title: string;

  /**
   * The currently active filter
   */
  public activeFilter: string = '';

  /**
   * If any entry has changed
   */
  public anyEntryChanged: boolean = false;

  /**
   * The current state of the bulk action component
   */
  public bulkActionState: BulkActionState = {
    added: [],
    removed: [],
    unchanged: [],
  };

  /**
   * A map of all entries
   */
  public bulkEntryMap: StringMap<BulkActionEntry>;

  /**
   * List of all entries
   */
  public bulkEntries: BulkActionEntry[];

  /**
   * Collapsed
   */
  public collapsed: boolean = true;

  /**
   * Depends on the states of anyEntryChanged, processing and replay
   */
  public disabled: boolean = true;

  /**
   * Entries that match the current filter of the filter-input
   */
  public filteredEntries: BulkActionEntry[] = [];

  /**
   *
   */
  public filterIsActive: boolean = false;

  /**
   * If the bulk actions have been processed
   */
  public hasBeenProcessed: boolean = false;

  /**
   * The entries for the current page
   */
  public pagedEntries: BulkActionEntry[] = [];

  /**
   * The current page
   */
  public pageNumber: number = 1;

  /**
   * The page size
   */
  public pageSize: number = 10;

  /**
   * How many pages exist
   */
  public pages: number = 1;

  /**
   * How many actions are currently being processed
   */
  public processing: number = 0;

  /**
   * How many actions failed
   */
  public processingFailures: number = 0;

  /**
   * How many actions are currently being processed
   */
  public processingActive: number = 0;

  /**
   * How many actions succeeded
   */
  public processingSuccesses: number = 0;

  /**
   * The width of the failure progressbar (Angular won't show a string in the style attribute so we need to create a SafeStyle object)
   */
  public progressFailure: SafeStyle;

  /**
   * The width of the success progressbar (Angular won't show a string in the style attribute so we need to create a SafeStyle object)
   */
  public progressSuccess: SafeStyle;

  /**
   * Properties the user can filter by
   */
  public properties?: MetaDataProperty[];

  /**
   * Create the page object that is needed by the pagination component
   */
  public page: Page<BulkActionEntry>;

  /**
   * If only changed entries should be filtered
   */
  public showOnlyChanges: boolean = false;

  /**
   * Show only failed entries (can be toggled by a button in the filter bar that is only shown when the processing is completed)
   */
  public showOnlyFailures: boolean = false;

  /**
   * Entries that are currently visible after applying showOnlyFailures or filterOnlyChangedEntries
   */
  public visibleEntries: BulkActionEntry[] = [];

  /**
   * Convenience map of active entries (mapped whenever the input 'activeEntries' changes)
   */
  private activeEntryMap: StringMap<BulkActionInput>;

  /**
   * Logger for this component
   */
  private logger = loggerFor('BulkActionComponent');

  get hasProcessed(): boolean {
    return this.processingFailures > 0 || this.processingSuccesses > 0;
  }

  constructor(
    private bulkActionService: BulkActionService,
    private sanitizer: DomSanitizer,
    private unsavedChangesService: UnsavedChangesService
  ) {}

  /**
   * Refresh entries and state whenever the input 'activeEntries' or the input 'entries' changes
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {
    const entriesChange = changes['entries'];
    const activeEntriesChange = changes['activeEntries'];

    if (entriesChange !== undefined && entriesChange.isFirstChange() === false) {
      let activeEntries = this.activeEntries;

      if (activeEntriesChange !== undefined) {
        activeEntries = activeEntriesChange.currentValue;
      }

      this.initialiseEntries(entriesChange.currentValue, activeEntries);
    }
  }

  /**
   * Refresh entries and the state on initialisation
   */
  ngOnInit() {
    this.initialiseEntries(this.entries, this.activeEntries);

    this.bulkActionService.loadFilterProperties().subscribe((filterProperties) => {
      this.properties = filterProperties;
    });
  }

  public calculateMetaData = (endUser: BulkActionEntry): FilterableEntry =>
    this.bulkActionService.calculateMetaData(endUser);

  /**
   * Set the page and pageSize globally and update the entries that are shown
   * @param page
   * @param pageSize
   */
  public onPageChanged({ page, pageSize }) {
    // Remember the page and the pageSize because we need to synchronise the pagination on the top with the pagination on the bottom
    this.pageNumber = page;
    this.pageSize = pageSize;
    this.pages = Math.ceil(this.visibleEntries.length / pageSize) || 1;

    this.selectPage(page);
  }

  /**
   * Reset the state
   */
  public reset() {
    this.showOnlyChanges = false;
    this.showOnlyFailures = false;

    this.initialiseEntries(this.entries, this.activeEntries);
  }

  /**
   * Call the processing function for each entry that has to be updated
   */
  public submit() {
    const actions = this.bulkActionState.added.concat(this.bulkActionState.removed);

    this.hasBeenProcessed = true;
    this.processing = actions.length;
    this.processingActive = this.processing;
    this.processingFailures = 0;
    this.processingSuccesses = 0;

    actions.forEach((entry) => this.process(entry));
  }

  /**
   * Select all entries
   */
  public selectAll() {
    // Update all entries
    Object.values(this.bulkEntryMap).forEach((bulkEntry) => this.changeState(bulkEntry, true));

    this.refreshState();
  }

  /**
   * Select all filtered entries
   */
  public selectAllFiltered() {
    this.filteredEntries.forEach((bulkEntry) => this.changeState(bulkEntry, true));

    this.refreshState();
  }

  /**
   * Unselect all filtered entries
   */
  public unselectAllFiltered() {
    this.filteredEntries.forEach((bulkEntry) => this.changeState(bulkEntry, false));

    this.refreshState();
  }

  /**
   * Toggle the given entry
   * @param entry
   */
  public toggleEntryState(entry: BulkActionEntry) {
    if (this.hasBeenProcessed === false) {
      this.changeState(entry);

      this.refreshState();
    }
  }

  /**
   * Unselect all entries
   */
  public unselectAll() {
    // Update all entries
    Object.values(this.bulkEntryMap).forEach((bulkEntry) => this.changeState(bulkEntry, false));

    this.refreshState();
  }

  public updateEntries(entries: BulkActionEntry[]) {
    this.filterIsActive = entries.length !== this.bulkEntries.length;
    this.filteredEntries = entries.map((entry) => this.bulkEntryMap[entry.id]);
    this.updateVisibleEntries();
    this.updatePagination(1);
  }

  /**
   * Recalculate the pagination and go to the specified page. If no page is specified, the page
   * stays the same. If this is not possible (e.g. because of a filter change there could be less
   * pages), the last page is shown.
   * @param page
   */
  public updatePagination(page?: number) {
    this.pagedEntries = this.visibleEntries;
    this.pages = Math.ceil(this.visibleEntries.length / this.pageSize) || 1;

    if (page == null) {
      page = this.pageNumber;
    }

    if (this.pages > 0 && this.pages <= page) {
      page = this.pages;
    }

    this.selectPage(page);
  }

  /**
   * Update the filtered entries
   */
  public updateVisibleEntries() {
    this.visibleEntries = this.filteredEntries.filter((entry) => {
      let success = true;

      if (this.showOnlyChanges) {
        success = entry.state !== BulkActionEntryState.UNCHANGED || entry.success || entry.failure != null;
      }

      if (this.showOnlyFailures) {
        success = entry.failure !== undefined;
      }

      return success;
    });

    this.updatePagination();
  }

  /**
   * Change the state of the given bulk action entry according to the active mode
   * @param entry
   * @param active If the active state should be set
   */
  private changeState(entry: BulkActionEntry, active?: boolean) {
    if (this.mode === 'ADD') {
      this.updateEntryState(entry, BulkActionEntryState.ADDED, BulkActionEntryState.UNCHANGED, active);
    } else if (this.mode === 'REMOVE') {
      this.updateEntryState(entry, BulkActionEntryState.REMOVED, BulkActionEntryState.UNCHANGED, active);
    } else {
      if (active === false) {
        // Remove all entries
        if (entry.active === true) {
          entry.state = BulkActionEntryState.REMOVED;
        } else {
          entry.state = BulkActionEntryState.UNCHANGED;
        }
      } else if (active === true) {
        // Add all entries
        if (entry.active === false) {
          entry.state = BulkActionEntryState.ADDED;
        } else {
          entry.state = BulkActionEntryState.UNCHANGED;
        }
      } else {
        // Toggle entries

        // If the entry is already active it should be removed
        if (this.activeEntryMap[entry.id] !== undefined) {
          this.updateEntryState(entry, BulkActionEntryState.REMOVED, BulkActionEntryState.UNCHANGED, active);
        } else {
          // Otherwise it get's added
          this.updateEntryState(entry, BulkActionEntryState.ADDED, BulkActionEntryState.UNCHANGED, active);
        }
      }
    }
  }

  /**
   * Start processing the given entry
   * @param bulkActionEntry
   */
  private process(bulkActionEntry: BulkActionEntry) {
    bulkActionEntry.failure = undefined;
    bulkActionEntry.processing = true;

    this.processingFunction(
      bulkActionEntry,
      () => this.processingSucceeded(bulkActionEntry),
      (error) => this.processingFailed(bulkActionEntry, error)
    );
  }

  /**
   * Called from outside after an entry failed to update
   * @param entry
   * @param error Why did it fail?
   */
  private processingFailed(entry: BulkActionEntry, error: PortalError) {
    --this.processingActive;
    ++this.processingFailures;

    entry.failure = error;
    entry.processing = false;

    this.refreshAnyChanged();
    this.refreshProgress();
  }

  /**
   * Called from outside after an entry was successfully updated
   * @param entry
   */
  private processingSucceeded(entry: BulkActionEntry) {
    --this.processingActive;
    ++this.processingSuccesses;

    entry.processing = false;
    entry.state = BulkActionEntryState.UNCHANGED;
    entry.success = true;

    this.refreshAnyChanged();
    this.refreshProgress();
  }

  /**
   * Create bulkEntries (based on the input 'entries' and set initial values)
   * @param entries
   * @param activeEntries
   */
  private initialiseEntries(entries: BulkActionInput[], activeEntries: BulkActionInput[]) {
    this.anyEntryChanged = false;
    this.disabled = false;
    this.processing = 0;

    this.bulkEntries = [];
    this.bulkEntryMap = {};

    // If the mode is ADD_REMOVE we need to know which entries should be removed and which should be added)
    // For this we fill the activeEntryMap (it's easier to access single values)
    if (this.mode === 'ADD_REMOVE') {
      this.activeEntryMap = toMap(activeEntries, 'id');
    } else {
      this.activeEntryMap = {};
    }

    for (const entry of entries) {
      const bulkEntry = {
        active: this.activeEntryMap.hasOwnProperty(entry.id),
        content: templateRefToText(this.bodyTemplate, {
          $implicit: entry,
        }).toLowerCase(),
        entry,
        failure: undefined,
        header: templateRefToText(this.headerTemplate, {
          $implicit: entry,
        }).toLowerCase(),
        id: entry.id,
        processing: false,
        state: BulkActionEntryState.UNCHANGED,
        success: false,
      };

      this.bulkEntries.push(bulkEntry);
      this.bulkEntryMap[entry.id] = bulkEntry;
    }

    this.refreshState();
  }

  private refreshAnyChanged() {
    this.anyEntryChanged = Object.values(this.bulkEntryMap).reduce(
      (a, b) => a || b.state !== BulkActionEntryState.UNCHANGED,
      false
    );

    if (
      (this.anyEntryChanged && this.hasProcessed === false) ||
      (this.processingFailures > 0 && this.replay)
    ) {
      this.unsavedChangesService.askForConfirmation();
    } else {
      this.unsavedChangesService.reset();
    }
  }

  /**
   * Recalculate the current progress
   */
  private refreshProgress() {
    this.progressFailure = this.sanitizer.bypassSecurityTrustStyle(
      `width: ${(this.processingFailures / this.processing) * 100}%`
    );

    this.progressSuccess = this.sanitizer.bypassSecurityTrustStyle(
      `width: ${(this.processingSuccesses / this.processing) * 100}%`
    );
  }

  /**
   * Calculate the state of the component
   */
  private refreshState() {
    this.refreshAnyChanged();

    this.bulkActionState = {
      added: [],
      removed: [],
      unchanged: [],
    };

    Object.values(this.bulkEntryMap).forEach((change) => {
      if (change.state === BulkActionEntryState.UNCHANGED) {
        this.bulkActionState.unchanged.push(change);
      } else if (change.state === BulkActionEntryState.ADDED) {
        this.bulkActionState.added.push(change);
      } else {
        this.bulkActionState.removed.push(change);
      }
    });

    this.updateVisibleEntries();

    this.logger.debug(
      'Updated state, element is now',
      this.anyEntryChanged ? 'changed' : 'unchanged',
      'and the new state is: ',
      this.bulkActionState
    );
  }

  /**
   * Go to the given page. If it doesn't exist then the closest page to it is shown (either the first or the last)
   * @param page
   */
  private selectPage(page: number) {
    const from = (page - 1) * this.pageSize;
    const to = page * this.pageSize;

    this.pageNumber = page;
    this.pagedEntries = this.visibleEntries.slice(from, to);

    this.updatePage();
  }

  /**
   * Toggle the state for the given entry
   * @param entry
   * @param activeState
   * @param inactiveState
   * @param active If the active state should be set
   */
  private updateEntryState(
    entry: BulkActionEntry,
    activeState: BulkActionEntryState,
    inactiveState: BulkActionEntryState,
    active?: boolean
  ) {
    // Toggle if active is undefined
    if (active === undefined) {
      if (entry.state === activeState) {
        entry.state = inactiveState;
      } else {
        entry.state = activeState;
      }
    } else if (active === true) {
      entry.state = activeState;
    } else {
      entry.state = inactiveState;
    }

    this.logger.debug(`Entry state changed to ${entry.state}`);
  }

  private updatePage(): void {
    const from = (this.pageNumber - 1) * this.pageSize;
    const to = from + this.pageSize;

    const filtered = this.visibleEntries.length;

    this.page = {
      allEntries: [],
      filteredEntries: [],
      filtered,
      from: from + 1,
      entries: [],
      page: this.pageNumber,
      pageSize: this.pageSize,
      pages: this.pages,
      to: to > filtered ? filtered : to,
      total: this.entries.length,
    };
  }
}
